'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { memo, useCallback, useMemo } from 'react'
import { cx } from 'class-variance-authority'
import { CaretDoubleLeftIcon } from '@phosphor-icons/react/dist/ssr/CaretDoubleLeft'

import { Link, type NavItem, navItems } from '@/navigation'
import { useUserContext } from '@/context/UserContext'
import { TeamSelector } from '@/features/team-selection/ui/TeamSelector'
import { DocLink, FlagGate } from '@/components/atoms'
import { useCurrentNavItem } from '@/hooks/useCurrentNavItem'

export const Navbar = ({
  collapsed,
  toggleCollapsedAction,
  showNavbar,
}: {
  readonly collapsed: boolean
  readonly toggleCollapsedAction: () => void
  readonly showNavbar: boolean
}) => {
  const { team, authorization, isClient } = useUserContext()
  const t = useTranslations('Navigation')

  const { currentNavItem } = useCurrentNavItem()

  const isAuthorized = useCallback(
    (navigation: NavItem) => {
      // During SSR or before client hydration, always show as unauthorized to prevent hydration mismatch
      if (!isClient) return false
      return (
        !navigation.authorizedRoles ||
        navigation.authorizedRoles.includes(authorization?.user?.role as string)
      )
    },
    [authorization?.user?.role, isClient],
  )

  const navbarItems = useMemo(() => {
    // Hide Organization if user has no access to master organization
    let newNavigation = [...navItems]
    if (team?.isMaster) {
      newNavigation = navItems.filter((navigation) => navigation.key !== 'team')
    } else {
      newNavigation = navItems.filter(
        (navigation) => navigation.key !== 'organization',
      )
    }
    if (!team?.master) {
      newNavigation = newNavigation.map((item) => {
        return {
          ...item,
          children: item.children?.filter(
            (child) => child.key !== 'resources-general',
          ),
        }
      })
    } else {
      newNavigation = newNavigation.map((item) => {
        return {
          ...item,
          children: item.children?.map((child) => {
            if (child.key === 'resources-general') {
              return {
                ...child,
                label: `Ressources ${team.master?.name}`,
              }
            }
            if (child.key === 'resources') {
              if (team?.parent?.id === team?.master?.id) {
                return {
                  ...child,
                  label: `Ressources ${team?.name}`,
                }
              }
              return {
                ...child,
                label: `Ressources ${team?.parent?.name}`,
              }
            }
            return child
          }),
        }
      })
    }
    return newNavigation
  }, [team])

  const groupedItems = useMemo(() => {
    const filtered = navbarItems.filter((item) => item.display === 'navbar')
    const groups: { key: string; items: NavItem[] }[] = []
    let currentGroup = ''
    for (const item of filtered) {
      if (item.group !== currentGroup) {
        currentGroup = item.group || ''
        groups.push({ key: currentGroup, items: [item] })
      } else {
        groups[groups.length - 1].items.push(item)
      }
    }
    return groups
  }, [navbarItems])

  return (
    <div
      className={cx(
        'fixed z-50 flex h-[100dvh] flex-col overflow-x-hidden bg-slate-700 text-gray-300 transition-all duration-300 ease-in-out md:static md:z-0 md:translate-x-0',
        {
          'w-64': !collapsed,
          'w-[72px]': collapsed,
          '-translate-x-full': !showNavbar,
        },
      )}
    >
      <div className="flex h-[var(--app-header-height)] items-center justify-between border-b border-slate-600/50 px-3">
        <button
          type="button"
          aria-label={t('toggleNavbar')}
          onClick={toggleCollapsedAction}
          className="shrink-0 cursor-pointer rounded-full border-0 bg-transparent p-0"
        >
          <Image
            src="/img/skop-icon.svg"
            height={40}
            width={40}
            alt="Skop"
            priority
            className="size-[var(--app-sidebar-logo-size)]"
          />
        </button>
        {!collapsed && (
          <div className="flex items-center gap-2">
            <DocLink component="navbar" />
            <button
              type="button"
              aria-label={t('toggleNavbar')}
              className="hidden size-9 place-content-center rounded-full hover:bg-slate-600 md:grid"
              onClick={toggleCollapsedAction}
            >
              <CaretDoubleLeftIcon className="size-4" />
            </button>
          </div>
        )}
      </div>
      <div className="border-b border-slate-600/50 px-3 py-[var(--app-sidebar-section-padding-y)]">
        <TeamSelector navbarCollapsed={collapsed} />
      </div>
      <nav className="flex-1 overflow-y-auto p-[var(--app-sidebar-nav-padding)]">
        {groupedItems.map((group) => (
          <div key={group.key}>
            {group.key !== 'main' && (
              <div className="mx-3 my-2 h-px bg-slate-500" />
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li
                  key={item.path}
                  className={cx('transition-all duration-300 ease-in-out', {
                    'h-[var(--app-sidebar-item-height)]': collapsed,
                  })}
                >
                  <FlagGate flagKey={item.featureFlagKey}>
                    <NavbarItem
                      item={item}
                      currentNavItem={currentNavItem}
                      navbarCollapsed={collapsed}
                      toggleNavbar={toggleCollapsedAction}
                      isAuthorized={isAuthorized}
                    />
                  </FlagGate>
                  {!collapsed && (
                    <FlagGate flagKey={item.featureFlagKey}>
                      <NavbarSubItems
                        item={item}
                        currentNavItem={currentNavItem}
                      />
                    </FlagGate>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </div>
  )
}

const isAncestor = (item: NavItem, targetKey: string): boolean => {
  return !!item.children?.some(
    (child) => child.key === targetKey || isAncestor(child, targetKey),
  )
}

const isCurrentPath = (item: NavItem, currentNavItem?: NavItem) => {
  if (!currentNavItem) return false

  // Exact match
  if (item.key === currentNavItem.key) return true

  // Ancestor match
  if (isAncestor(item, currentNavItem.key)) return true

  // Back button fallback (explicit linkage)
  if (currentNavItem.backButton === item.path) return true
  if (currentNavItem.parent?.backButton === item.path) return true

  return false
}

const NavbarItem = memo(
  ({
    item,
    currentNavItem,
    navbarCollapsed,
    toggleNavbar,
    isAuthorized,
  }: {
    item: NavItem
    currentNavItem?: NavItem
    navbarCollapsed: boolean
    toggleNavbar: () => void
    isAuthorized: (navigation: NavItem) => boolean
  }) => {
    const t = useTranslations('Navigation')
    const authorized = isAuthorized(item)
    const selected = isCurrentPath(item, currentNavItem)

    return (
      <Link
        href={authorized ? item.path : '#'}
        title={navbarCollapsed ? t(item.key) : undefined}
        aria-selected={selected}
        onClick={() => selected && toggleNavbar()}
        className={cx(
          'flex items-center rounded-lg text-sm font-medium outline-orange-500 transition-all duration-200 ease-in-out aria-selected:bg-slate-600/50 aria-selected:text-orange-500',
          {
            'h-full justify-center px-0 py-2': navbarCollapsed,
            'gap-3 px-3 py-2': !navbarCollapsed,
            'cursor-not-allowed text-slate-100': !authorized,
            'cursor-pointer hover:bg-slate-600 hover:text-gray-100': authorized,
          },
        )}
      >
        {item.icon}
        {!navbarCollapsed && (
          <span className="line-clamp-1">{t(item.key)}</span>
        )}
      </Link>
    )
  },
)

NavbarItem.displayName = 'NavbarItem'

const isSubItemActive = (
  item: NavItem,
  currentNavItem: NavItem | undefined,
): boolean => {
  if (!currentNavItem) return false
  if (item.path === currentNavItem.path) {
    return true
  }
  if (currentNavItem.display !== 'navbar') {
    // Prefer a static backButton for matching — it explicitly points to the correct parent sub-item
    if (currentNavItem.backButton && !currentNavItem.backButton.includes('[')) {
      return item.path === currentNavItem.backButton
    }
    // Fall back to parent path when backButton is absent or dynamic
    return item.path === currentNavItem.parent?.path
  }
  return false
}

const NavbarSubItems = memo(
  ({
    item,
    currentNavItem,
  }: {
    item: NavItem
    currentNavItem: NavItem | undefined
  }) => {
    const t = useTranslations('Navigation')
    const isUnderItemPath =
      currentNavItem?.path === item.path ||
      currentNavItem?.path?.startsWith(item.path + '/') ||
      isCurrentPath(item, currentNavItem)
    if (
      (!item.children?.length || !item.parent?.children?.length) &&
      !isUnderItemPath
    )
      return null

    const items = item.children || item.parent?.children || []

    return (
      <ul className="mt-0.5 space-y-0.5">
        {items
          .filter((child) => child.display === 'navbar')
          .map((child) => (
            <li key={child.path} className="relative">
              {child.childGroup && (
                <div className="mx-3 my-2 h-px bg-slate-500" />
              )}
              <Link
                href={child.path}
                aria-selected={isSubItemActive(child, currentNavItem)}
                className="ml-10 block rounded-md py-1 pl-3 text-[13px] text-slate-100 outline-orange-500 transition-all duration-200 ease-in-out hover:bg-slate-600/30 hover:text-gray-100 aria-selected:bg-slate-600/30 aria-selected:text-orange-500"
              >
                <span className="line-clamp-1">{t(child.key)}</span>
              </Link>
            </li>
          ))}
      </ul>
    )
  },
)

NavbarSubItems.displayName = 'NavbarSubItems'
